import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export type UpgradeRequestStatus = 'pending' | 'granted' | 'declined' | 'cancelled';
export type CustomerType = 'user' | 'team';

export interface AdminUpgradeRequest {
  id: string;
  customerType: CustomerType;
  customerId: string;
  customerLabel: string | null;
  requestedPlanCode: string;
  requestedBy: string;
  requestedByName: string | null;
  requestedByEmail: string | null;
  note: string | null;
  status: UpgradeRequestStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  grantedSubscriptionId: string | null;
  createdAt: string;
}

export interface ListUpgradeRequestsResult {
  total: number;
  items: AdminUpgradeRequest[];
}

export interface ListUpgradeRequestsQuery {
  limit?: number;
  offset?: number;
  status?: UpgradeRequestStatus;
}

function buildQS(q: ListUpgradeRequestsQuery): string {
  const sp = new URLSearchParams();
  if (q.limit !== undefined) sp.set('limit', String(q.limit));
  if (q.offset !== undefined) sp.set('offset', String(q.offset));
  if (q.status) sp.set('status', q.status);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function adminUpgradeRequestsKey(query: ListUpgradeRequestsQuery = {}): QueryKey {
  return ['admin', 'upgrade-requests', query];
}

export function useAdminUpgradeRequests(query: ListUpgradeRequestsQuery) {
  return useQuery({
    queryKey: adminUpgradeRequestsKey(query),
    queryFn: () =>
      api<ListUpgradeRequestsResult>(`/api/admin/upgrade-requests${buildQS(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useGrantUpgradeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      durationDays,
      planCode,
      reason,
    }: {
      id: string;
      durationDays: number | null;
      planCode?: string | undefined;
      reason: string;
    }) =>
      api<AdminUpgradeRequest>(`/api/admin/upgrade-requests/${id}/grant`, {
        method: 'POST',
        body: planCode ? { durationDays, planCode, reason } : { durationDays, reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'badges'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'upgrade-requests'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
  });
}

export function useDeclineUpgradeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<AdminUpgradeRequest>(`/api/admin/upgrade-requests/${id}/decline`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'badges'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'upgrade-requests'] });
    },
  });
}
